import React from 'react';
import { Form, Field } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, ButtonTypes } from '../../../common/Button';
import { validateSettings } from '../../../utils/reduxFormUtils';
import { updateSettings, initiateVerification } from '../actions';
import { apiClient } from '../../../utils/apiClient';
import {
  DuplicateUserCheckQuery,
  UpdateSettingsRequestBody
} from '../../../../common/types';
import { RootState } from '../../../reducers';
import type { AccountForm as AccountFormType } from '../../../utils/reduxFormUtils';

function asyncValidate(fieldToValidate: 'username' | 'email', value: string) {
  if (!value || value.trim().length === 0) {
    return '';
  }
  const queryParams: DuplicateUserCheckQuery = {
    check_type: fieldToValidate
  };
  queryParams[fieldToValidate] = value;
  return apiClient
    .get('/signup/duplicate_check', { params: queryParams })
    .then((response) => {
      if (response.data.exists) {
        return response.data.message;
      }
      return '';
    });
}

export function AccountForm() {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const handleInitiateVerification = (evt: React.MouseEvent) => {
    evt.preventDefault();
    dispatch(initiateVerification());
  };

  function validateUsername(username: AccountFormType['username']) {
    if (username === user.username) return '';
    return asyncValidate('username', username);
  }

  function validateEmail(email: AccountFormType['email']) {
    if (email === user.email) return '';
    return asyncValidate('email', email);
  }

  function onSubmit(formProps: UpdateSettingsRequestBody) {
    return dispatch(updateSettings(formProps));
  }

  return (
    <Form
      fields={['username', 'email', 'currentPassword', 'newPassword']}
      validate={validateSettings}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, submitting, invalid, form }) => (
        <form
          className="form"
          onSubmit={async (event: React.FormEvent) => {
            const result = await handleSubmit(event);
            const currentValues = form.getState().values;
            form.restart({
              username: currentValues?.username ?? user.username,
              email: currentValues?.email ?? user.email
            });
            return result;
          }}
        >
          <Field
            name="email"
            validate={validateEmail}
            validateFields={[]}
            initialValue={user.email}
          >
            {(field) => (
              <p className="form__field">
                <label htmlFor="email" className="form__label">
                  {t('AccountForm.Email')}
                </label>
                <input
                  className="form__input"
                  aria-label={t('AccountForm.EmailARIA')}
                  type="email"
                  id="email"
                  autoComplete="email"
                  {...field.input}
                />
                {field.meta.touched && field.meta.error && (
                  <span className="form-error" aria-live="polite">
                    {field.meta.error}
                  </span>
                )}
              </p>
            )}
          </Field>
          {user.verified !== 'verified' && (
            <p className="form__context">
              <span className="form__status">
                {t('AccountForm.Unconfirmed')}
              </span>
              {user.emailVerificationInitiate === true ? (
                <span className="form__status">
                  {' '}
                  {t('AccountForm.EmailSent')}
                </span>
              ) : (
                <Button
                  onClick={handleInitiateVerification}
                  className="form__resend-button"
                >
                  {t('AccountForm.Resend')}
                </Button>
              )}
            </p>
          )}
          <Field
            name="username"
            validate={validateUsername}
            validateFields={[]}
            initialValue={user.username}
          >
            {(field) => (
              <p className="form__field">
                <label htmlFor="username" className="form__label">
                  {t('AccountForm.UserName')}
                </label>
                <input
                  className="form__input"
                  aria-label={t('AccountForm.UserNameARIA')}
                  type="text"
                  id="username"
                  autoComplete="username"
                  {...field.input}
                />
                {field.meta.touched && field.meta.error && (
                  <span className="form-error" aria-live="polite">
                    {field.meta.error}
                  </span>
                )}
              </p>
            )}
          </Field>
          {user.github === undefined && user.google === undefined && (
            <Field name="currentPassword">
              {(field) => (
                <p className="form__field">
                  <label htmlFor="currentPassword" className="form__label">
                    {t('AccountForm.CurrentPassword')}
                  </label>
                  <input
                    className="form__input"
                    aria-label={t('AccountForm.CurrentPasswordARIA')}
                    type="password"
                    id="currentPassword"
                    autoComplete="current-password"
                    {...field.input}
                  />
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </p>
              )}
            </Field>
          )}
          {user.github === undefined && user.google === undefined && (
            <Field name="newPassword">
              {(field) => (
                <p className="form__field">
                  <label htmlFor="newPassword" className="form__label">
                    {t('AccountForm.NewPassword')}
                  </label>
                  <input
                    className="form__input"
                    aria-label={t('AccountForm.NewPasswordARIA')}
                    type="password"
                    id="newPassword"
                    autoComplete="new-password"
                    {...field.input}
                  />
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </p>
              )}
            </Field>
          )}
          <Button type={ButtonTypes.SUBMIT} disabled={submitting || invalid}>
            {t('AccountForm.SaveAccountDetails')}
          </Button>
        </form>
      )}
    </Form>
  );
}
